"use client";

import React, { useEffect, useState } from "react";
import {
  IonPage,
  IonContent,
  IonHeader,
  IonToolbar,
} from "@ionic/react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  Button,
  IconButton,
  Chip,
  Tooltip,
  Alert,
  LinearProgress,
  Fade,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Avatar,
} from "@mui/material";
import PrintIcon from '@mui/icons-material/Print';
import DownloadIcon from '@mui/icons-material/Download';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import RefreshIcon from '@mui/icons-material/Refresh';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import BusinessIcon from '@mui/icons-material/Business';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, subYears } from 'date-fns';
import axios from "axios";
import { requestID, baseUrl, config } from "../../../../common/utils";

// Define types based on your new API response structure
interface BalanceSheetItem {
  property_plant_equipment_res: number;
  trade_other_receivables_res: number;
  prepayments: number;
  inventories: number;
  cash_equivalent: number;
  accummulated_funds: number;
  minority_interest: number;
  profit_loss: number;
  capital_grant: number;
  loan_borrow: number;
  loan_borrow_bank: number;
  trade_payable: number;
  tax_payable: number;
}

interface BalanceSheetResponse {
  status: boolean;
  data: BalanceSheetItem;
  "data.previous"?: BalanceSheetItem;
}

const BalanceSheetReport: React.FC = () => {
  // State management
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [balanceSheetData, setBalanceSheetData] = useState<BalanceSheetItem | null>(null);
  const [previousPeriodData, setPreviousPeriodData] = useState<BalanceSheetItem | null>(null);
  const [expandedSections, setExpandedSections] = useState<string[]>(['assets', 'equity', 'liabilities']);

  // Helper functions
  const formatCurrency = (value: number) => {
    const isNegative = value < 0;
    const absValue = Math.abs(value);
    
    return (
      <span style={{ color: isNegative ? '#d32f2f' : 'inherit' }}>
        {isNegative ? '-' : ''}
        {new Intl.NumberFormat('en-NG', {
          style: 'currency',
          currency: 'NGN',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(absValue)}
      </span>
    );
  };

  const getTrendIcon = (current: number, previous: number) => {
    const change = current - previous;
    if (change > 0) return <TrendingUpIcon color="success" fontSize="small" />;
    if (change < 0) return <TrendingDownIcon color="error" fontSize="small" />;
    return <TrendingFlatIcon color="action" fontSize="small" />;
  };

  const calculateChange = (current: number, previous: number) => {
    if (previous === 0) return { value: current, percent: 0 };
    const change = current - previous;
    const percent = (change / Math.abs(previous)) * 100;
    return { value: change, percent };
  };

  // Format date for display
  const formatDateDisplay = (date: Date) => {
    return format(date, 'dd MMMM yyyy');
  };

  const formatDateAPI = (date: Date) => {
    return format(date, 'yyyy-MM-dd');
  };

  // Group data into balance sheet categories and calculate section totals
  const getGroupedData = () => {
    if (!balanceSheetData) return {
      assets: { 
        nonCurrent: [],
        current: [],
        nonCurrentTotal: { current: 0, previous: 0 },
        currentTotal: { current: 0, previous: 0 }
      },
      equity: [],
      equityTotal: { current: 0, previous: 0 },
      liabilities: { 
        nonCurrent: [],
        current: [],
        nonCurrentTotal: { current: 0, previous: 0 },
        currentTotal: { current: 0, previous: 0 }
      }
    };

    // Assets - Non-Current
    const nonCurrentAssets = [
      {
        name: 'Property, Plant & Equipment',
        current: balanceSheetData.property_plant_equipment_res || 0,
        previous: previousPeriodData?.property_plant_equipment_res || 0
      }
    ];

    // Calculate Non-Current Assets Total
    const nonCurrentAssetsTotal = {
      current: nonCurrentAssets.reduce((sum, item) => sum + item.current, 0),
      previous: nonCurrentAssets.reduce((sum, item) => sum + item.previous, 0)
    };

    // Assets - Current
    const currentAssets = [
      {
        name: 'Trade & Other Receivables',
        current: balanceSheetData.trade_other_receivables_res || 0,
        previous: previousPeriodData?.trade_other_receivables_res || 0
      },
      {
        name: 'Prepayments',
        current: balanceSheetData.prepayments || 0,
        previous: previousPeriodData?.prepayments || 0
      },
      {
        name: 'Inventories',
        current: balanceSheetData.inventories || 0,
        previous: previousPeriodData?.inventories || 0
      },
      {
        name: 'Cash & Cash Equivalents',
        current: balanceSheetData.cash_equivalent || 0,
        previous: previousPeriodData?.cash_equivalent || 0
      }
    ];

    // Calculate Current Assets Total
    const currentAssetsTotal = {
      current: currentAssets.reduce((sum, item) => sum + item.current, 0),
      previous: currentAssets.reduce((sum, item) => sum + item.previous, 0)
    };

    // Equity
    const equity = [
      {
        name: 'Accumulated Funds',
        current: balanceSheetData.accummulated_funds || 0,
        previous: previousPeriodData?.accummulated_funds || 0
      },
      {
        name: 'Minority Interest',
        current: balanceSheetData.minority_interest || 0,
        previous: previousPeriodData?.minority_interest || 0
      },
      {
        name: 'Accumulated Profit/Loss',
        current: balanceSheetData.profit_loss || 0,
        previous: previousPeriodData?.profit_loss || 0
      }
    ];

    // Calculate Equity Total
    const equityTotal = {
      current: equity.reduce((sum, item) => sum + item.current, 0),
      previous: equity.reduce((sum, item) => sum + item.previous, 0)
    };

    // Liabilities - Non-Current
    const nonCurrentLiabilities = [
      {
        name: 'Capital Grant',
        current: balanceSheetData.capital_grant || 0,
        previous: previousPeriodData?.capital_grant || 0
      },
      {
        name: 'Loans & Borrowings',
        current: balanceSheetData.loan_borrow || 0,
        previous: previousPeriodData?.loan_borrow || 0
      }
    ];

    // Calculate Non-Current Liabilities Total
    const nonCurrentLiabilitiesTotal = {
      current: nonCurrentLiabilities.reduce((sum, item) => sum + item.current, 0),
      previous: nonCurrentLiabilities.reduce((sum, item) => sum + item.previous, 0)
    };

    // Liabilities - Current
    const currentLiabilities = [
      {
        name: 'Loans & Borrowings (Bank Loan)',
        current: balanceSheetData.loan_borrow_bank || 0,
        previous: previousPeriodData?.loan_borrow_bank || 0
      },
      {
        name: 'Trade & Other Payables',
        current: balanceSheetData.trade_payable || 0,
        previous: previousPeriodData?.trade_payable || 0
      },
      {
        name: 'Tax Payable',
        current: balanceSheetData.tax_payable || 0,
        previous: previousPeriodData?.tax_payable || 0
      }
    ];

    // Calculate Current Liabilities Total
    const currentLiabilitiesTotal = {
      current: currentLiabilities.reduce((sum, item) => sum + item.current, 0),
      previous: currentLiabilities.reduce((sum, item) => sum + item.previous, 0)
    };

    return {
      assets: { 
        nonCurrent: nonCurrentAssets, 
        current: currentAssets,
        nonCurrentTotal: nonCurrentAssetsTotal,
        currentTotal: currentAssetsTotal
      },
      equity,
      equityTotal,
      liabilities: { 
        nonCurrent: nonCurrentLiabilities, 
        current: currentLiabilities,
        nonCurrentTotal: nonCurrentLiabilitiesTotal,
        currentTotal: currentLiabilitiesTotal
      }
    };
  };

  // Calculate totals
  const calculateTotals = () => {
    if (!balanceSheetData) return { 
      totalAssets: 0, 
      totalEquity: 0,
      totalLiabilities: 0 
    };

    // Total Assets
    const totalAssets = 
      (balanceSheetData.property_plant_equipment_res || 0) +
      (balanceSheetData.trade_other_receivables_res || 0) +
      (balanceSheetData.prepayments || 0) +
      (balanceSheetData.inventories || 0) +
      (balanceSheetData.cash_equivalent || 0);

    // Total Equity
    const totalEquity = 
      (balanceSheetData.accummulated_funds || 0) +
      (balanceSheetData.minority_interest || 0) +
      (balanceSheetData.profit_loss || 0);

    // Total Liabilities
    const totalLiabilities = 
      (balanceSheetData.capital_grant || 0) +
      (balanceSheetData.loan_borrow || 0) +
      (balanceSheetData.loan_borrow_bank || 0) +
      (balanceSheetData.trade_payable || 0) +
      (balanceSheetData.tax_payable || 0);

    return { totalAssets, totalEquity, totalLiabilities };
  };

  const getNetBalances = async () => {
    setLoading(true);
    setError(null);

    const formData = {
      requestID: requestID(),
      coa_ids: ["coa_6970f88b524944.37142009", "coa_6970fa41446088.93886338"],
      as_at_date: formatDateAPI(selectedDate),
      endDate: formatDateAPI(selectedDate)
    };

    try {
      const response = await axios.post<BalanceSheetResponse>(
        baseUrl() + "Geacloud_ChartOfAccounts/getNetBalances_balanceSheet",
        formData,
        config
      );

      if (response.data.status) {
        setBalanceSheetData(response.data.data);
        if (response.data["data.previous"]) {
          setPreviousPeriodData(response.data["data.previous"]);
        }
      } else {
        setError("Failed to fetch balance sheet data");
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch balance sheet data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Toggle section expansion
  const toggleSection = (section: string) => {
    setExpandedSections(prev =>
      prev.includes(section)
        ? prev.filter(s => s !== section)
        : [...prev, section]
    );
  };

  // Handle export
  const handleExport = (format: 'pdf' | 'excel') => {
    // Export logic here
    console.log(`Exporting as ${format}`);
  };

  // Handle date change
  const handleDateChange = (date: Date | null) => {
    if (date) {
      setSelectedDate(date);
    }
  };

  useEffect(() => {
    getNetBalances();
  }, [selectedDate]);

  // Calculate totals from actual data
  const { totalAssets, totalEquity, totalLiabilities } = calculateTotals();
  const totalEquityLiabilities = totalEquity + totalLiabilities;
  
  // Calculate previous period totals if available
  const previousTotals = previousPeriodData ? {
    totalAssets: 
      (previousPeriodData.property_plant_equipment_res || 0) +
      (previousPeriodData.trade_other_receivables_res || 0) +
      (previousPeriodData.prepayments || 0) +
      (previousPeriodData.inventories || 0) +
      (previousPeriodData.cash_equivalent || 0),
    totalEquity: 
      (previousPeriodData.accummulated_funds || 0) +
      (previousPeriodData.minority_interest || 0) +
      (previousPeriodData.profit_loss || 0),
    totalLiabilities: 
      (previousPeriodData.capital_grant || 0) +
      (previousPeriodData.loan_borrow || 0) +
      (previousPeriodData.loan_borrow_bank || 0) +
      (previousPeriodData.trade_payable || 0) +
      (previousPeriodData.tax_payable || 0)
  } : { totalAssets: 0, totalEquity: 0, totalLiabilities: 0 };

  const isBalanced = Math.abs(totalAssets - totalEquityLiabilities) < 0.01;

  // Get grouped data for rendering
  const groupedData = getGroupedData();

  // Render category items with total row
  const renderCategoryWithTotal = (
    items: Array<{name: string, current: number, previous: number}>,
    total: {current: number, previous: number},
    sectionTitle: string,
    indent: number = 4,
    showTotalRow: boolean = true
  ) => {
    const sectionChange = calculateChange(total.current, total.previous);
    
    return (
      
      <>
        {/* Section Header */}
        <TableRow>
          <TableCell sx={{ pl: indent, fontWeight: 600, py: 1.5 }}>
            {sectionTitle}
          </TableCell>
          <TableCell align="right">-</TableCell>
          <TableCell align="right">-</TableCell>
          <TableCell align="right">-</TableCell>
        </TableRow>
        
        {/* Section Items */}
        {items.map((item, index) => {
          const change = calculateChange(item.current, item.previous);
          
          return (
            <TableRow key={`item-${index}`} hover>
              <TableCell sx={{ pl: indent + 2, color: 'text.secondary' }}>
                {item.name}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 500 }}>
                {formatCurrency(item.current)}
              </TableCell>
              <TableCell align="right">
                {formatCurrency(item.previous)}
              </TableCell>
              <TableCell align="right">
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                  {getTrendIcon(item.current, item.previous)}
                  <Typography
                    variant="body2"
                    color={
                      change.value > 0 ? 'success.main' : 
                      change.value < 0 ? 'error.main' : 'text.secondary'
                    }
                  >
                    {item.previous !== 0 ? `${change.percent.toFixed(1)}%` : 'N/A'}
                  </Typography>
                </Box>
              </TableCell>
            </TableRow>
          );
        })}
        
        {/* Section Total */}
        {showTotalRow && (
          <TableRow sx={{ bgcolor: 'grey.50' }}>
            <TableCell sx={{ pl: indent + 2, fontWeight: 600, py: 1.5 }}>
              Total {sectionTitle}
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 600 }}>
              {formatCurrency(total.current)}
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 600 }}>
              {formatCurrency(total.previous)}
            </TableCell>
            <TableCell align="right">
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                {getTrendIcon(total.current, total.previous)}
                <Chip
                  label={`${sectionChange.percent.toFixed(1)}%`}
                  size="small"
                  color={
                    sectionChange.value > 0 ? "success" : 
                    sectionChange.value < 0 ? "error" : "default"
                  }
                  sx={{ fontWeight: 600 }}
                />
              </Box>
            </TableCell>
          </TableRow>
        )}
      </>
    );
  };

  return (
    <IonPage className="pushDown">
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <IonHeader>
          <IonToolbar>
            <Box sx={{
              px: 3,
              py: 1.5,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              bgcolor: 'background.paper',
              borderBottom: '1px solid',
              borderColor: 'divider'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  <AccountBalanceIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: "primary.main" }}>
                    FINANCIAL STATEMENTS
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Real-time balance sheet monitoring
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Tooltip title="Refresh Data">
                  <IconButton
                    size="small"
                    onClick={getNetBalances}
                    disabled={loading}
                  >
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Export PDF">
                  <Button
                    variant="outlined"
                    startIcon={<PrintIcon />}
                    size="small"
                    onClick={() => handleExport('pdf')}
                    disabled={loading}
                  >
                    PDF
                  </Button>
                </Tooltip>

                <Tooltip title="Export Excel">
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    size="small"
                    onClick={() => handleExport('excel')}
                    disabled={loading}
                  >
                    Excel
                  </Button>
                </Tooltip>

                <Chip
                  icon={<FilterAltIcon />}
                  label="Filters"
                  size="small"
                  variant="outlined"
                  onClick={() => {/* Open filter dialog */ }}
                />
              </Box>
            </Box>
          </IonToolbar>
        </IonHeader>

        <IonContent>
          {loading && <LinearProgress />}

          <Box sx={{
            p: { xs: 2, md: 4 },
            bgcolor: 'background.default',
            minHeight: "100%"
          }}>
            {/* Header Card */}
            <Card sx={{
              mb: 3,
              borderRadius: 3,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              boxShadow: '0 10px 40px rgba(102, 126, 234, 0.2)'
            }}>
              <CardContent>
                <Grid container spacing={3} alignItems="center">
                  <Grid item xs={12} md={8}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                      <BusinessIcon fontSize="large" />
                      <Box>
                        <Typography variant="h4" fontWeight={800}>
                          EDO SPECIALIST HOSPITAL
                        </Typography>
                        <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
                          Statement of Financial Position
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                      <Box>
                        <Typography variant="caption" sx={{ opacity: 0.8 }}>
                          Report Period
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {formatDateDisplay(selectedDate)}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" sx={{ opacity: 0.8 }}>
                          Comparison Period
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {formatDateDisplay(subYears(selectedDate, 1))}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" sx={{ opacity: 0.8 }}>
                          Generated On
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {format(new Date(), 'dd MMM yyyy HH:mm')}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Card sx={{ 
                      bgcolor: 'rgba(255,255,255,0.15)', 
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255,255,255,0.2)'
                    }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box>
                            <Typography variant="caption" sx={{ opacity: 0.8 }}>
                              Total Assets
                            </Typography>
                            <Typography variant="h5" fontWeight={700}>
                              {formatCurrency(totalAssets)}
                            </Typography>
                            {previousPeriodData && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                {getTrendIcon(totalAssets, previousTotals.totalAssets)}
                                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                  {calculateChange(totalAssets, previousTotals.totalAssets).percent.toFixed(1)}% from previous
                                </Typography>
                              </Box>
                            )}
                          </Box>
                          <DatePicker
                            label="Statement Date"
                            value={selectedDate}
                            onChange={handleDateChange}
                            slots={{
                              openPickerIcon: CalendarMonthIcon,
                            }}
                            slotProps={{
                              textField: {
                                size: 'small',
                                sx: {
                                  '& .MuiInputBase-root': {
                                    bgcolor: 'white',
                                    borderRadius: 2,
                                    minWidth: '150px'
                                  }
                                }
                              }
                            }}
                          />
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Error Alert */}
            {error && (
              <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            {/* Quick Stats Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={4}>
                <Card sx={{ 
                  height: '100%', 
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  transition: 'transform 0.2s',
                  '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 }
                }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar sx={{ 
                        bgcolor: 'primary.50',
                        color: 'primary.main'
                      }}>
                        <AttachMoneyIcon />
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          Total Equity
                        </Typography>
                        <Typography variant="h6" fontWeight={700}>
                          {formatCurrency(totalEquity)}
                        </Typography>
                        {previousPeriodData && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                            {getTrendIcon(totalEquity, previousTotals.totalEquity)}
                            <Typography 
                              variant="caption" 
                              color={
                                calculateChange(totalEquity, previousTotals.totalEquity).value > 0 ? 'success.main' : 
                                calculateChange(totalEquity, previousTotals.totalEquity).value < 0 ? 'error.main' : 'text.secondary'
                              }
                            >
                              {calculateChange(totalEquity, previousTotals.totalEquity).percent.toFixed(1)}% from previous
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card sx={{ 
                  height: '100%', 
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  transition: 'transform 0.2s',
                  '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 }
                }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar sx={{ 
                        bgcolor: 'warning.50',
                        color: 'warning.main'
                      }}>
                        <AccountBalanceIcon />
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          Total Liabilities
                        </Typography>
                        <Typography variant="h6" fontWeight={700}>
                          {formatCurrency(totalLiabilities)}
                        </Typography>
                        {previousPeriodData && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                            {getTrendIcon(totalLiabilities, previousTotals.totalLiabilities)}
                            <Typography 
                              variant="caption" 
                              color={
                                calculateChange(totalLiabilities, previousTotals.totalLiabilities).value > 0 ? 'success.main' : 
                                calculateChange(totalLiabilities, previousTotals.totalLiabilities).value < 0 ? 'error.main' : 'text.secondary'
                              }
                            >
                              {calculateChange(totalLiabilities, previousTotals.totalLiabilities).percent.toFixed(1)}% from previous
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card sx={{ 
                  height: '100%', 
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: isBalanced ? 'success.light' : 'warning.light',
                  transition: 'transform 0.2s',
                  '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 }
                }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar sx={{ 
                        bgcolor: isBalanced ? 'success.50' : 'warning.50',
                        color: isBalanced ? 'success.main' : 'warning.main'
                      }}>
                        {isBalanced ? <CheckCircleIcon /> : <WarningIcon />}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          Balance Status
                        </Typography>
                        <Typography variant="h6" fontWeight={700} color={isBalanced ? 'success.main' : 'warning.main'}>
                          {isBalanced ? 'BALANCED' : 'IMBALANCED'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Assets: {formatCurrency(totalAssets)} | Liabilities+Equity: {formatCurrency(totalEquityLiabilities)}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Balance Sheet Table */}
            <Fade in={!loading && !!balanceSheetData}>
              <Card sx={{ 
                borderRadius: 3, 
                overflow: 'hidden',
                border: '1px solid',
                borderColor: 'divider'
              }}>
                <TableContainer component={Paper} elevation={0}>
                  <Table size="medium">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'primary.50' }}>
                        <TableCell sx={{
                          fontWeight: 700,
                          width: '50%',
                          color: 'primary.main',
                          fontSize: '0.95rem',
                          py: 2
                        }}>
                          FINANCIAL POSITION
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: 'primary.main', py: 2 }}>
                          Current Period (₦)
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: 'primary.main', py: 2 }}>
                          Previous Period (₦)
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: 'primary.main', py: 2 }}>
                          CHANGE
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {/* ASSETS SECTION */}
                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                        <TableCell colSpan={4} sx={{ py: 1.5 }}>
                          <Accordion
                            elevation={0}
                            expanded={expandedSections.includes('assets')}
                            onChange={() => toggleSection('assets')}
                            sx={{ 
                              '&:before': { display: 'none' },
                              '&.Mui-expanded': { margin: 0 }
                            }}
                          >
                            <AccordionSummary 
                              expandIcon={<ExpandMoreIcon />}
                              sx={{ 
                                minHeight: '48px',
                                '&.Mui-expanded': { minHeight: '48px' }
                              }}
                            >
                              <Typography variant="subtitle1" fontWeight={700} color="primary">
                                ASSETS
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: 0 }}>
                              <Table size="small">
                                <TableBody>
                                  {/* Non-Current Assets with Total */}
                                  {groupedData.assets.nonCurrent.length > 0 && 
                                    renderCategoryWithTotal(
                                      groupedData.assets.nonCurrent,
                                      groupedData.assets.nonCurrentTotal,
                                      "Non-Current Assets",
                                      4
                                    )
                                  }

                                  {/* Current Assets with Total */}
                                  {groupedData.assets.current.length > 0 && 
                                    renderCategoryWithTotal(
                                      groupedData.assets.current,
                                      groupedData.assets.currentTotal,
                                      "Current Assets",
                                      4
                                    )
                                  }

                                  {/* Total Assets */}
                                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                                    <TableCell sx={{ pl: 4, fontWeight: 700, py: 2 }}>
                                      TOTAL ASSETS
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700, py: 2 }}>
                                      {formatCurrency(totalAssets)}
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700, py: 2 }}>
                                      {formatCurrency(previousTotals.totalAssets)}
                                    </TableCell>
                                    <TableCell align="right" sx={{ py: 2 }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                                        {getTrendIcon(totalAssets, previousTotals.totalAssets)}
                                        <Chip
                                          label={`${calculateChange(totalAssets, previousTotals.totalAssets).percent.toFixed(1)}%`}
                                          size="small"
                                          color={
                                            calculateChange(totalAssets, previousTotals.totalAssets).value > 0 ? "success" : 
                                            calculateChange(totalAssets, previousTotals.totalAssets).value < 0 ? "error" : "default"
                                          }
                                          sx={{ fontWeight: 600 }}
                                        />
                                      </Box>
                                    </TableCell>
                                  </TableRow>
                                </TableBody>
                              </Table>
                            </AccordionDetails>
                          </Accordion>
                        </TableCell>
                      </TableRow>

                      {/* EQUITY SECTION */}
                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                        <TableCell colSpan={4} sx={{ py: 1.5 }}>
                          <Accordion
                            elevation={0}
                            expanded={expandedSections.includes('equity')}
                            onChange={() => toggleSection('equity')}
                            sx={{ 
                              '&:before': { display: 'none' },
                              '&.Mui-expanded': { margin: 0 }
                            }}
                          >
                            <AccordionSummary 
                              expandIcon={<ExpandMoreIcon />}
                              sx={{ 
                                minHeight: '48px',
                                '&.Mui-expanded': { minHeight: '48px' }
                              }}
                            >
                              <Typography variant="subtitle1" fontWeight={700} color="success.main">
                                EQUITY
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: 0 }}>
                              <Table size="small">
                                <TableBody>
                                  {/* Equity Items */}
                                  {groupedData.equity.map((item, index) => {
                                    const change = calculateChange(item.current, item.previous);
                                    
                                    return (
                                      <TableRow key={`equity-${index}`} hover>
                                        <TableCell sx={{ pl: 4, color: 'text.secondary' }}>
                                          {item.name}
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 500 }}>
                                          {formatCurrency(item.current)}
                                        </TableCell>
                                        <TableCell align="right">
                                          {formatCurrency(item.previous)}
                                        </TableCell>
                                        <TableCell align="right">
                                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                                            {getTrendIcon(item.current, item.previous)}
                                            <Typography
                                              variant="body2"
                                              color={
                                                change.value > 0 ? 'success.main' : 
                                                change.value < 0 ? 'error.main' : 'text.secondary'
                                              }
                                            >
                                              {item.previous !== 0 ? `${change.percent.toFixed(1)}%` : 'N/A'}
                                            </Typography>
                                          </Box>
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}

                                  {/* Total Equity */}
                                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                                    <TableCell sx={{ pl: 4, fontWeight: 700, py: 2 }}>
                                      Total Equity
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700, py: 2 }}>
                                      {formatCurrency(totalEquity)}
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700, py: 2 }}>
                                      {formatCurrency(previousTotals.totalEquity)}
                                    </TableCell>
                                    <TableCell align="right" sx={{ py: 2 }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                                        {getTrendIcon(totalEquity, previousTotals.totalEquity)}
                                        <Chip
                                          label={`${calculateChange(totalEquity, previousTotals.totalEquity).percent.toFixed(1)}%`}
                                          size="small"
                                          color={
                                            calculateChange(totalEquity, previousTotals.totalEquity).value > 0 ? "success" : 
                                            calculateChange(totalEquity, previousTotals.totalEquity).value < 0 ? "error" : "default"
                                          }
                                          sx={{ fontWeight: 600 }}
                                        />
                                      </Box>
                                    </TableCell>
                                  </TableRow>
                                </TableBody>
                              </Table>
                            </AccordionDetails>
                          </Accordion>
                        </TableCell>
                      </TableRow>

                      {/* LIABILITIES SECTION */}
                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                        <TableCell colSpan={4} sx={{ py: 1.5 }}>
                          <Accordion
                            elevation={0}
                            expanded={expandedSections.includes('liabilities')}
                            onChange={() => toggleSection('liabilities')}
                            sx={{ 
                              '&:before': { display: 'none' },
                              '&.Mui-expanded': { margin: 0 }
                            }}
                          >
                            <AccordionSummary 
                              expandIcon={<ExpandMoreIcon />}
                              sx={{ 
                                minHeight: '48px',
                                '&.Mui-expanded': { minHeight: '48px' }
                              }}
                            >
                              <Typography variant="subtitle1" fontWeight={700} color="error.main">
                                LIABILITIES
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: 0 }}>
                              <Table size="small">
                                <TableBody>
                                  {/* Non-Current Liabilities with Total */}
                                  {groupedData.liabilities.nonCurrent.length > 0 && 
                                    renderCategoryWithTotal(
                                      groupedData.liabilities.nonCurrent,
                                      groupedData.liabilities.nonCurrentTotal,
                                      "Non-Current Liabilities",
                                      4
                                    )
                                  }

                                  {/* Current Liabilities with Total */}
                                  {groupedData.liabilities.current.length > 0 && 
                                    renderCategoryWithTotal(
                                      groupedData.liabilities.current,
                                      groupedData.liabilities.currentTotal,
                                      "Current Liabilities",
                                      4
                                    )
                                  }

                                  {/* Total Liabilities */}
                                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                                    <TableCell sx={{ pl: 4, fontWeight: 700, py: 2 }}>
                                      Total Liabilities
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700, py: 2 }}>
                                      {formatCurrency(totalLiabilities)}
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700, py: 2 }}>
                                      {formatCurrency(previousTotals.totalLiabilities)}
                                    </TableCell>
                                    <TableCell align="right" sx={{ py: 2 }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                                        {getTrendIcon(totalLiabilities, previousTotals.totalLiabilities)}
                                        <Chip
                                          label={`${calculateChange(totalLiabilities, previousTotals.totalLiabilities).percent.toFixed(1)}%`}
                                          size="small"
                                          color={
                                            calculateChange(totalLiabilities, previousTotals.totalLiabilities).value > 0 ? "success" : 
                                            calculateChange(totalLiabilities, previousTotals.totalLiabilities).value < 0 ? "error" : "default"
                                          }
                                          sx={{ fontWeight: 600 }}
                                        />
                                      </Box>
                                    </TableCell>
                                  </TableRow>
                                </TableBody>
                              </Table>
                            </AccordionDetails>
                          </Accordion>
                        </TableCell>
                      </TableRow>

                      {/* GRAND TOTAL */}
                      <TableRow sx={{
                        bgcolor: isBalanced ? 'success.main' : 'warning.main',
                        '& .MuiTableCell-root': {
                          color: 'white',
                          borderBottom: 'none',
                          py: 3
                        }
                      }}>
                        <TableCell sx={{ fontWeight: 800, fontSize: '1.1rem' }}>
                          TOTAL EQUITY & LIABILITIES
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 800, fontSize: '1.1rem' }}>
                          {formatCurrency(totalEquityLiabilities)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 800, fontSize: '1.1rem' }}>
                          {formatCurrency(previousTotals.totalEquity + previousTotals.totalLiabilities)}
                        </TableCell>
                        <TableCell align="right">
                          <Chip
                            label={isBalanced ? "BALANCED ✓" : "IMBALANCE ⚠"}
                            size="small"
                            sx={{
                              bgcolor: 'white',
                              color: isBalanced ? 'success.main' : 'warning.main',
                              fontWeight: 700,
                              '& .MuiChip-label': { px: 1.5 }
                            }}
                            icon={isBalanced ? <CheckCircleIcon /> : <WarningIcon />}
                          />
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Footer Notes */}
                <Box sx={{ 
                  p: 2, 
                  bgcolor: 'grey.50', 
                  borderTop: '1px solid',
                  borderColor: 'divider'
                }}>
                  <Typography variant="caption" color="text.secondary">
                    <strong>Notes:</strong> All figures in Nigerian Naira (₦). This statement has been prepared in accordance with IFRS.
                    Report generated on {format(new Date(), 'dd MMMM yyyy')}. For detailed notes, refer to full financial statements.
                  </Typography>
                </Box>
              </Card>
            </Fade>

            {/* Legend */}
            <Box sx={{ 
              mt: 3, 
              p: 2, 
              bgcolor: 'grey.50', 
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider'
            }}>
              <Typography variant="caption" fontWeight={600} color="text.primary" sx={{ mb: 1, display: 'block' }}>
                LEGEND:
              </Typography>
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TrendingUpIcon color="success" fontSize="small" />
                  <Typography variant="caption">Increase from previous period</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TrendingDownIcon color="error" fontSize="small" />
                  <Typography variant="caption">Decrease from previous period</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TrendingFlatIcon color="action" fontSize="small" />
                  <Typography variant="caption">No change from previous period</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span style={{ color: '#d32f2f' }}>₦</span>
                  <Typography variant="caption">Negative value (shown in red)</Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </IonContent>
      </LocalizationProvider>
    </IonPage>
  );
};

export default BalanceSheetReport;